<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useDebounceFn } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import MessageItem from './MessageItem.vue';
import { channelDisplayName } from '../helpers/timeline';

const props = defineProps({
  channel: { type: Object, required: true },
  currentUserId: { type: Number, default: null },
  statusFor: { type: Function, required: true },
  nameFor: { type: Function, required: true },
});

const emit = defineEmits(['open']);

const { t } = useI18n();
const store = useStore();

const MIN_LENGTH = 2;
const PER_PAGE = 20; // SEARCH_PER_PAGE on the server
const DEBOUNCE_MS = 300;

const inputRef = ref(null);
const term = ref('');
const allChannels = ref(false);
const results = ref([]);
const page = ref(1);
const hasMore = ref(false);
const isLoading = ref(false);

const isTooShort = computed(() => term.value.trim().length < MIN_LENGTH);

const run = async ({ append = false } = {}) => {
  if (isTooShort.value) {
    results.value = [];
    hasMore.value = false;
    return;
  }
  const query = term.value.trim();
  isLoading.value = true;
  try {
    const data = await store.dispatch('teamChat/searchMessages', {
      q: query,
      channelId: allChannels.value ? null : props.channel.id,
      page: page.value,
    });
    // A slower answer for an older query must not overwrite the current one.
    if (query !== term.value.trim()) return;
    results.value = append ? [...results.value, ...data] : data;
    hasMore.value = data.length >= PER_PAGE;
  } catch {
    useAlert(t('TEAM_CHAT.SEARCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const search = () => {
  page.value = 1;
  run();
};
const debouncedSearch = useDebounceFn(search, DEBOUNCE_MS);
const loadMore = () => {
  page.value += 1;
  run({ append: true });
};

watch(term, debouncedSearch);
watch([allChannels, () => props.channel.id], search);
onMounted(() => inputRef.value?.focus());
</script>

<template>
  <div class="flex flex-col h-full min-h-0">
    <div class="flex-shrink-0 px-4 pt-3 space-y-2">
      <label
        class="flex items-center gap-2 px-3 rounded-lg h-9 bg-n-alpha-2 text-n-slate-10 focus-within:ring-1 focus-within:ring-n-slate-8"
      >
        <span class="flex-shrink-0 size-4 i-lucide-search" />
        <input
          ref="inputRef"
          v-model="term"
          type="search"
          class="w-full !m-0 !p-0 text-sm !bg-transparent !border-0 !outline-none !shadow-none text-n-slate-12 placeholder:text-n-slate-10"
          :placeholder="t('TEAM_CHAT.SEARCH_PLACEHOLDER')"
        />
      </label>
      <div class="flex gap-1 p-0.5 rounded-lg bg-n-alpha-1 text-[0.8125rem]">
        <button
          v-for="option in [false, true]"
          :key="String(option)"
          type="button"
          class="flex-1 py-1 rounded-md"
          :class="
            allChannels === option
              ? 'bg-n-solid-1 shadow-sm font-medium text-n-slate-12'
              : 'text-n-slate-11'
          "
          @click="allChannels = option"
        >
          {{
            option
              ? t('TEAM_CHAT.SEARCH_ALL_CHANNELS')
              : t('TEAM_CHAT.SEARCH_THIS_CHANNEL')
          }}
        </button>
      </div>
    </div>

    <div class="flex-1 min-h-0 px-4 py-3 overflow-y-auto">
      <p v-if="isTooShort" class="py-8 text-sm text-center text-n-slate-10">
        {{ t('TEAM_CHAT.SEARCH_HINT') }}
      </p>
      <div
        v-else-if="isLoading && !results.length"
        class="flex justify-center py-8"
      >
        <span
          class="size-5 i-lucide-loader-circle animate-spin text-n-slate-10"
        />
      </div>
      <p
        v-else-if="!results.length"
        class="py-8 text-sm text-center text-n-slate-10"
      >
        {{ t('TEAM_CHAT.SEARCH_EMPTY') }}
      </p>
      <div v-else class="space-y-2">
        <button
          v-for="message in results"
          :key="message.id"
          type="button"
          class="block w-full p-2 border rounded-xl text-start border-n-weak hover:bg-n-alpha-1"
          @click="emit('open', message)"
        >
          <span
            v-if="allChannels"
            class="flex items-center gap-1 mb-1 text-xs font-medium text-n-slate-11"
          >
            <span
              class="size-3"
              :class="message.channel.private ? 'i-lucide-lock' : 'i-lucide-hash'"
            />
            {{ channelDisplayName(message.channel).replace(/^#/, '') }}
          </span>
          <MessageItem
            :message="message"
            :status="statusFor(message.user_id ?? message.user?.id)"
            :current-user-id="currentUserId"
            :name-for="nameFor"
            compact
            class="pointer-events-none"
          />
        </button>
        <button
          v-if="hasMore"
          type="button"
          class="w-full py-2 text-sm rounded-lg text-n-slate-11 hover:bg-n-alpha-1"
          :disabled="isLoading"
          @click="loadMore"
        >
          {{ t('TEAM_CHAT.SEARCH_MORE') }}
        </button>
      </div>
    </div>
  </div>
</template>
